import { useState, useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  useFetchIndexFundPriceHistory
} from '@/hooks/securities/indexfunds/useIndexFundPriceHistory.ts';
import {IndexFundPriceHistory} from "@/integrations/supabase/indexfunds.models.ts";
import {getDateForTimeRange} from "@timeUtils";

const TIME_RANGES: TimeRange[] = ['1W', '1M', '1Y', 'ALL'];

/**
 * Filters price history records to only those within the selected period.
 * Exported so it can be tested independently (Property 9).
 */
export function filterByPeriod(
  records: IndexFundPriceHistory[],
  period: TimeRange,
  now: Date = new Date()
): IndexFundPriceHistory[] {
  const cutoff = getDateForTimeRange(period, now);
  if (!cutoff) return records;
  return records.filter((r) => new Date(r.fetched_at) >= cutoff);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function CustomTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const { date, price } = payload[0].payload;
  return (
    <div className="bg-background border border-border rounded-lg shadow-lg p-3">
      <p className="text-sm text-muted-foreground mb-1">
        {new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
      </p>
      <p className="text-lg font-semibold">{formatCurrency(price)}</p>
    </div>
  );
}

interface IndexFundPortfolioTrendChartProps {
  fundSymbol?: string;
}

export function IndexFundPortfolioTrendChart({ fundSymbol = '' }: IndexFundPortfolioTrendChartProps) {
  const [period, setPeriod] = useState<TimeRange>('1M');

  const { data: priceHistory = [], isLoading } = useFetchIndexFundPriceHistory(fundSymbol, period);

  const chartData = useMemo(
    () =>
      [...priceHistory]
        .sort((a, b) => new Date(a.fetched_at).getTime() - new Date(b.fetched_at).getTime())
        .map((r) => ({ date: r.fetched_at, price: r.price })),
    [priceHistory]
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base font-semibold">Portfolio Value Over Time</CardTitle>
        <div className="flex gap-1" role="group" aria-label="Time range filter">
          {TIME_RANGES.map((range) => (
            <Button
              key={range}
              variant={period === range ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPeriod(range)}
              aria-pressed={period === range}
            >
              {range}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-[300px] flex items-end justify-between gap-2 px-4" aria-busy="true">
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="w-full bg-muted animate-pulse rounded-t"
                style={{ height: `${40 + (i % 5) * 12}%` }}
              />
            ))}
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center" data-testid="empty-state">
            <p className="text-muted-foreground text-sm">
              No price history data available for the selected period.
            </p>
          </div>
        ) : (
          <div
            role="img"
            aria-label={`Index fund portfolio value trend chart for ${period} period with ${chartData.length} data points`}
          >
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <defs>
                  <linearGradient id="indexFundPortfolioGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="date" tickFormatter={formatDate} className="text-xs" />
                <YAxis tickFormatter={formatCurrency} className="text-xs" />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="price"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  fill="url(#indexFundPortfolioGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
